import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAdminUsers } from "@/hooks/queries";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";
import type { AdminUser } from "@/types";

function formatCop(cents: number): string {
  return "$" + Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function matches(u: AdminUser, q: string): boolean {
  if (!q) return true;
  const hay = `${u.fullName} ${u.documentType} ${u.documentNumber} ${u.email} ${u.phone} ${
    u.subscriber?.name ?? ""
  }`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

export function UsersList() {
  const navigate = useNavigate();
  const query = useAdminUsers();
  const [search, setSearch] = useState("");

  const all = useMemo(() => query.data?.users ?? [], [query.data]);
  const filtered = useMemo(() => all.filter((u) => matches(u, search.trim())), [all, search]);

  const withSub = all.filter((u) => u.subscriber).length;
  const paying = all.filter((u) => u.payments.succeeded > 0).length;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
        <p className="text-sm text-slate-500 mt-1">
          Everyone who has logged in and verified their identity — with or without a subscription.
        </p>
      </div>

      {!query.isPending && !query.isError && (
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Total users" value={all.length} />
          <Stat label="With subscription" value={withSub} />
          <Stat label="Paying" value={paying} />
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name, document, phone, or email"
          className="pl-8"
        />
      </div>

      <Card className="overflow-hidden">
        {query.isPending ? (
          <CenteredSpinner label="Loading users…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={search ? "No results" : "No users yet"}
            description={
              search
                ? "Try a different filter."
                : "Users appear here after they log in and verify their identity."
            }
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>User</TH>
                <TH>Document</TH>
                <TH>Subscription</TH>
                <TH>Certificates</TH>
                <TH>Payments</TH>
                <TH>Joined</TH>
              </tr>
            </THead>
            <TBody>
              {filtered.map((u) => (
                <TR
                  key={u.userId}
                  onClick={
                    u.subscriber ? () => navigate(`/subscribers/${u.subscriber!.id}`) : undefined
                  }
                  className={u.subscriber ? "cursor-pointer" : undefined}
                >
                  <TD>
                    <p className="font-medium text-slate-900">{u.fullName}</p>
                    <p className="text-[11px] text-slate-500 font-mono">
                      +{u.phone} · {u.email}
                    </p>
                  </TD>
                  <TD className="text-slate-900">
                    {u.documentType} {u.documentNumber}
                  </TD>
                  <TD>
                    {u.subscriber ? (
                      <Badge tone={u.subscriber.type === "juridica" ? "brand" : "info"}>
                        {u.subscriber.type}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400">Sin suscripción</span>
                    )}
                  </TD>
                  <TD>
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                        {u.certificates.inProgress} en curso
                      </span>
                      <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">
                        {u.certificates.completed} emitidos
                      </span>
                    </div>
                  </TD>
                  <TD>
                    {u.payments.succeeded > 0 ? (
                      <div className="text-[11px]">
                        <span className="font-medium text-slate-900">
                          {u.payments.succeeded} pago(s)
                        </span>
                        <span className="text-slate-500"> · {formatCop(u.payments.totalPaidCents)}</span>
                      </div>
                    ) : u.payments.attempts > 0 ? (
                      <span className="text-[11px] text-slate-400">
                        {u.payments.attempts} intento(s)
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </TD>
                  <TD className="text-[11px] text-slate-500">{formatDate(u.createdAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-2xl font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
